<script>
    import { onMount } from "svelte";
    import {
        instanceStatusData,
        assessmentDefinitionId,
        exerciseSections,
    } from "../../utilities/DataStore.js";
    import { api } from "../../utilities/Utils.svelte";
    import {
        buildExerciseSections,
        computeFinishState,
        computeSectionFills,
        computeSectionStates,
    } from "../categorySections.mjs";

    // Section progress bar driven by the definition's categories: each category
    // that holds exercises is one dot, in the order the definition lists them.
    // ProgressDetails2 derived the same dots from exercise priorities, which
    // only survived while authors kept them in tidy 100-blocks.

    export let instanceId;

    let loadingState = "NEW";

    // [{ name, sectionNum, categoryId, exercises }]
    let sections = [];
    let sectionsLoaded = false;

    onMount(() => {
        initialize();
    });

    function initialize() {
        Promise.all([loadInstanceStatus(), loadSections()]);
    }

    function loadInstanceStatus() {
        loadingState = "LOADING";
        return api
            .get(`public/assessment/instance/${instanceId}/status`)
            .json()
            .then((data) => {
                $instanceStatusData = data;
                loadingState = "OK";
            })
            .catch(() => {
                loadingState = "ERROR";
            });
    }

    // The exercises carry the category they belong to, the category list carries
    // the order and the names, so both are needed before the dots can be drawn.
    function loadSections() {
        if (!$assessmentDefinitionId) return Promise.resolve();

        return Promise.all([
            api
                .get(`public/assessment/definition/${$assessmentDefinitionId}/exercise`)
                .json(),
            api
                .get(`public/assessment/definition/${$assessmentDefinitionId}/category`)
                .json()
                .catch((error) => {
                    console.warn("Could not load assessment categories for section progress", error);
                    return [];
                }),
        ])
            .then(([exerciseList, categoryList]) => {
                sections = buildExerciseSections(exerciseList, categoryList);
                sectionsLoaded = sections.length > 0;
                // AssessmentExercise reads this to title the INTRO page.
                $exerciseSections = sections;
            })
            .catch((error) => {
                console.warn("Could not load exercise list for section progress", error);
            });
    }

    $: doneCount = $instanceStatusData?.countUniqueDone || 0;
    $: sectionStates = computeSectionStates(sections, doneCount);
    // One segment per part, each leading out of its dot and into the next one.
    $: sectionFills = computeSectionFills(sections, doneCount);
    $: finishState = computeFinishState(sections, doneCount);
</script>

{#if $instanceStatusData !== undefined && loadingState !== "ERROR"}
    <div class="multistep-progress">
        {#if sectionsLoaded}
            <!-- Each part draws its dot and the segment leading out of it; the
                 closing dot ends the track, so parts and segments match up. -->
            <div class="steps-container">
                {#each sections as sec, i}
                    <div class="step-item">
                        <div class="step-dot {sectionStates[i]}">
                            {#if sectionStates[i] === "done"}
                                <svg viewBox="0 0 12 12" width="11" height="11" aria-hidden="true">
                                    <polyline points="1.5,6 4.5,9 10.5,3" fill="none" stroke="white" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            {:else}
                                {sec.sectionNum ?? (sec.categoryId === null ? "" : i + 1)}
                            {/if}
                        </div>
                        {#if sec.name}
                            <span class="step-label {sectionStates[i]}">{sec.name.toUpperCase()}</span>
                        {/if}
                    </div>
                    <div class="step-connector" style={`--fill:${sectionFills[i] || 0}%;`}></div>
                {/each}

                <div class="step-item">
                    <div class="step-dot step-dot-finish {finishState}">
                        <!-- Finish flag, in the dot's own colour so it reads on
                             both the pending and the completed dot. -->
                        <svg viewBox="0 0 12 12" width="12" height="12" aria-hidden="true">
                            <path d="M2.9 1.3 V10.7" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                            <path d="M2.9 2.3 H9.1 L7.6 4.5 L9.1 6.7 H2.9 Z" fill="currentColor" stroke="currentColor" stroke-width="0.7" stroke-linejoin="round"/>
                        </svg>
                    </div>
                </div>
            </div>
        {/if}
    </div>
{/if}

<style>
    .multistep-progress {
        width: 100%;
        padding: 1.75rem 1.5rem 0.5rem;
    }

    /* ── Steps track ── */
    .steps-container {
        display: flex;
        align-items: flex-start;
        width: 100%;
    }

    .step-item {
        display: flex;
        flex-direction: column;
        align-items: center;
        flex-shrink: 0;
        max-width: 110px;
    }

    .step-connector {
        flex: 1;
        height: 2px;
        margin-top: 16px; /* vertically center with the 34px circle */
        background-color: #d1dde8;
        border-radius: 2px;
        position: relative;
        overflow: hidden;
        min-width: 16px;
    }

    .step-connector::after {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        width: var(--fill, 0%);
        background-color: #00a094;
        border-radius: 2px;
        transition: width 0.35s ease;
    }

    /* ── Dot ── */
    .step-dot {
        width: 34px;
        height: 34px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 0.8rem;
        font-weight: 700;
        flex-shrink: 0;
        transition: background-color 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;
    }

    .step-dot.done {
        background-color: #00a094;
        border: 2px solid #00a094;
        color: white;
    }

    .step-dot.active {
        background-color: #00a094;
        border: 2px solid #00a094;
        color: white;
        box-shadow: 0 0 0 4px rgba(0, 160, 148, 0.18);
    }

    .step-dot.pending {
        background-color: #ffffff;
        border: 2px solid #c8d8e4;
        color: #9eb4c0;
    }

    /* ── Narrow screens ──
       The closing dot adds a dot and a segment to the track, which pushed a
       five-part assessment past the width of the smallest phones. Tightening
       the dots and the gutters buys that back and then some. */
    @media (max-width: 400px) {
        .multistep-progress {
            padding-left: 0.75rem;
            padding-right: 0.75rem;
        }

        .step-dot {
            width: 28px;
            height: 28px;
            font-size: 0.72rem;
        }

        .step-connector {
            margin-top: 13px; /* vertically center with the 28px circle */
            min-width: 10px;
        }
    }

    /* ── Label ── */
    /* Category names are available now, but the bar still shows dots only -
       turning this on is the one change needed to label them. */
    .step-label {
        display: none;
    }

    .step-label.active { color: #00a094; }
    .step-label.done   { color: #00a094; }
    .step-label.pending { color: #9eb4c0; }
</style>
